import { BufferLoader } from "./buffer-loader";
import { frequencies } from "./frequencies";

let berkleePrefix = "https://pulse.berklee.edu/content/tools/eqTrainer/";
export interface Master {
  level: number;
  quizFrequency: number;
  quizGain: number;
  samplePath: string;
  userAudioURL: string;
  userAudioLoaded: boolean;
  userAudioName: string;
  sampleRate: number;
  extension: string;
  songBuffers: AudioBuffer[];
  audioSamples: string[];
  selectedSample: number;
  selectedBuffer?: AudioBufferSourceNode;
  atx?: AudioContext;
  compressor?: DynamicsCompressorNode;
  biquadFilter?: BiquadFilterNode;
  analyser?: AnalyserNode;
  dataArray?: any;
  filterFrequency: number;
  filterGain: number;
  filterType: BiquadFilterType;
  filterQ: number;
  detuneCheck: AudioBuffer[];
  initialized: boolean;
  initializing: boolean;
  playing: boolean;
  masterGain?: GainNode;
  masterVolume?: GainNode;
  masterVolumeAmount: number;
}
export const master: Master = {
  level: 1,
  quizFrequency: 500,
  quizGain: 12,
  samplePath: berkleePrefix + "samples/", // use this for livew
  userAudioURL: "",
  userAudioLoaded: false,
  userAudioName: "No User File",
  sampleRate: 44100,
  filterFrequency: 500,
  filterGain: 0,
  filterType: "peaking",
  filterQ: 1,
  extension: ".mp3",
  songBuffers: [],
  selectedSample: 0,
  // audioSamples: ["Air", "Gold", "Indigo"],
  audioSamples: [
    "pink-noise",
    "a-little-bit-funky-blues",
    "aquasordido",
    "montuno",
    "soul-shuffle-2",
    "wade-in-the-water",
    "drum-kit",
    "piano",
    "guitar",
    "lead-vocal",
    "backing-vocals",
  ],
  detuneCheck: [],
  initialized: false,
  initializing: false,
  playing: false,
  masterVolumeAmount: 1,
};

export function init(): void {
  if (!master.initialized) {
    master.initializing = true;
    // show loading message
    master.atx = new AudioContext({ sampleRate: master.sampleRate });
    master.compressor = master.atx.createDynamicsCompressor();
    master.biquadFilter = master.atx.createBiquadFilter();
    master.biquadFilter.type = master.filterType;
    master.masterGain = master.atx.createGain();
    master.masterVolume = master.atx.createGain();
    master.analyser = master.atx.createAnalyser();
    masterChainSettings();
    makeWhiteNoise();
    loadAudio();

    setQuiz();
    updateFilter("A");

    master.initialized = true;
    master.initializing = false;
  }
}

export function initAnalyser() {
  if (master.analyser) {
    master.analyser.getByteTimeDomainData(master.dataArray);
  }
}

export function masterChainSettings() {
  if (
    master.atx &&
    master.analyser &&
    master.compressor &&
    master.biquadFilter &&
    master.masterGain &&
    master.masterVolume
  ) {
    master.biquadFilter.connect(master.masterGain);

    master.compressor.threshold.value = -5; //play around with this number
    master.compressor.knee.value = 3;
    master.compressor.ratio.value = 20; //highest ratio
    master.compressor.attack.value = 0.003;
    master.compressor.release.value = 0.25;
    master.compressor.connect(master.masterVolume);
    master.compressor.connect(master.analyser);

    master.masterVolume.connect(master.atx.destination);
    master.masterGain.gain.setValueAtTime(1, 0);
    master.masterGain.connect(master.compressor);
  }
}

export function updateMasterVolume() {
  if (master.masterVolume) {
    master.masterVolume.gain.setValueAtTime(master.masterVolumeAmount, 0);
  }
}
export function updateFilter(filterState: string) {
  if (master.biquadFilter) {
    if (filterState === "B") {
      master.biquadFilter.frequency.value = master.filterFrequency;
      master.biquadFilter.gain.value = master.filterGain;
    } else if (filterState === "A") {
      master.biquadFilter.frequency.value = master.quizFrequency;
      master.biquadFilter.gain.value = master.quizGain;
    }
  }
}

export function loadAudio() {
  if (master.atx) {
    var bufferLoader: any;
    var sampleURLList: string[] = [];
    for (let i = 0; i < master.audioSamples.length; i++) {
      var thisPath = master.samplePath + master.audioSamples[i] + master.extension;
      sampleURLList.push(thisPath);
    }
    if (master.userAudioURL !== "") {
      sampleURLList.push(master.userAudioURL);
    }
    bufferLoader = new BufferLoader(master.atx, sampleURLList, songBuffersLoaded);
    bufferLoader.load();
  }
}
export function loadUserAudio() {
  if (master.atx) {
    var bufferLoader: any;
    bufferLoader = new BufferLoader(master.atx, [master.userAudioURL], replaceUserSong);
    bufferLoader.load();
  }
}

export function replaceUserSong(bufferList: AudioBuffer[]): void {
  if (master.userAudioLoaded) {
    master.songBuffers.splice(master.songBuffers.length - 1, 1, ...bufferList);
  } else {
    master.songBuffers.push(...bufferList);
  }
  master.userAudioLoaded = true;
}
export function songBuffersLoaded(bufferList: AudioBuffer[]): void {
  master.songBuffers.push(...bufferList);
}

export function makeWhiteNoise() {
  if (master.atx) {
    const duration = 2;
    const bufferSize = duration * master.sampleRate;
    const channels = 2;
    var whiteNoiseBuffer = master.atx.createBuffer(2, bufferSize, master.sampleRate);
    var rand;
    for (var i = 0; i < bufferSize; i++) {
      for (var j = 0; j < channels; j++) {
        rand = Math.random() * 2 - 1; // -1 to 1
        whiteNoiseBuffer.getChannelData(j)[i] = rand;
      }
    }
    master.songBuffers.push(whiteNoiseBuffer);
  }
}

export function pauseSound(): void {
  if (master.selectedBuffer && master.atx) {
    master.selectedBuffer.stop(master.atx.currentTime);
    master.playing = false;
  }
}
export function playSound(): void {
  if (master.atx && master.songBuffers && master.biquadFilter) {
    master.selectedBuffer = master.atx.createBufferSource();
    master.selectedBuffer.loop = true;
    master.selectedBuffer.buffer = master.songBuffers[master.selectedSample];
    if (master.selectedSample === 0) {
      let whiteNoiseGain = master.atx.createGain();
      whiteNoiseGain.gain.setValueAtTime(0.1, 0);
      master.selectedBuffer.connect(whiteNoiseGain);
      whiteNoiseGain.connect(master.biquadFilter);
    } else {
      master.selectedBuffer.connect(master.biquadFilter);
    }
    master.selectedBuffer.start(master.atx.currentTime);
    master.playing = true;
  }
}

export function checkQuiz() {
  if (master.quizFrequency === master.filterFrequency && master.quizGain === master.filterGain) {
    return true;
  } else {
    return false;
  }
}
export function setQuiz(): void {
  let gainRanges = [
    [12, 24],
    [-12, -24],
    [-24, -12, 12, 24],
  ][master.level - 1];
  const previousQuiz = [master.quizFrequency, master.quizGain];

  while (previousQuiz[0] === master.quizFrequency && previousQuiz[1] === master.quizGain) {
    master.quizFrequency = frequencies[getRndInteger(0, frequencies.length - 1)];
    master.quizGain = gainRanges[getRndInteger(0, gainRanges.length - 1)];
  }
  master.filterFrequency = 500;
  master.filterGain = 0;
}

export function getRndInteger(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
